/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { featureEnabled } from 'utils';
import styled from 'styled-components';
import {
  Button,
  Card,
  Icon,
  AntdModal,
  Spin,
  Text,
  theme,
} from '@brokerbay/design-components';
import { Icon as WebElementsIcon } from '@brokerbay/elements-web';
import { SupraSystemSubscriptionType } from '@bb/domain/dist/types';
import { userSubRolesToLabelMap } from '@bb/users';
import { SubscriptionsCardContext } from './SubscriptionsCardContext';
import ManagePlanModal from './ManagePlanModal';

const SUBSCRIPTION_LABEL: Record<
  | 'default'
  | 'answering'
  | 'LiveConciergePlus'
  | 'AppointmentDesk'
  | SupraSystemSubscriptionType,
  Record<string, string>
> = {
  answering: {
    name: 'Live Concierge Service',
    subHeader: '24 / 7 / 365 Live Concierge Service',
  },
  default: { name: 'BrokerBay Showing System', subHeader: 'Full Ecosystem' },
  LiveConciergePlus: { name: 'BrokerBay Live Concierge' },
  AppointmentDesk: { name: 'BrokerBay Appointment Desk' },
  FULL: { name: 'Full', subHeader: 'Supra Key' },
  LIMITED: { name: 'Limited', subHeader: 'Supra Key' },
  MANAGED_ACCESS: { name: 'Managed Access', subHeader: 'Supra Key' },
};

export type ContentProps = {
  answeringSubscriptionLevel: 'org' | 'user';
  isMlsCovered?: boolean;
  isBoardCovered?: boolean;
  useLiveConciergePlus?: boolean;
  hasValidStripeMlsProductId: boolean; // Checking whether the mlsProductId is a valid Stripe ID.
  conciergeEnabled?: boolean;
};

const Content: React.FC<ContentProps> = ({
  isMlsCovered,
  isBoardCovered,
  useLiveConciergePlus,
  hasValidStripeMlsProductId,
  conciergeEnabled,
}) => {
  const {
    subscriptions,
    loadingSubscription,
    cancellingSubscription,
    cancelSubscription,
    showPlanManage,
    orgPlanPrice,
    canManageSubscription,
  } = useContext(SubscriptionsCardContext);

  const [isManagePlanOpen, setManagePlanOpen] = useState(false);
  useEffect(() => () => AntdModal.destroyAll(), []);

  const onCancelSubscription = (id: string) => {
    const { response, message } = canManageSubscription;

    if (!response) {
      AntdModal.error({
        title: 'You are not authorized to manage subscriptions.',
        content: message,
        centered: true,
      });
      return;
    }

    AntdModal.confirm({
      title: (
        <>
          Are you sure you’d like to cancel your live concierge service
          subscription?
        </>
      ),
      okText: 'Yes',
      centered: true,
      async onOk() {
        await cancelSubscription(id);
      },
    });
  };

  // Render out "cards" for each of the subscriptions if they exist, or some simple text
  // if there are no subscriptions.
  const renderSubscriptions = useMemo(
    () =>
      subscriptions.length ? (
        subscriptions.map(subscription => {
          const { id, type, price, billingFrequency, status } = subscription;
          const serviceType =
            'serviceType' in subscription
              ? subscription.serviceType
              : undefined;
          const subRole =
            'subRole' in subscription ? subscription.subRole : undefined;

          /**
           * A utility function for generating the subscription name to display.
           */
          const displayCorrectSubscriptionName = () => {
            if (
              hasValidStripeMlsProductId &&
              serviceType &&
              type === 'answering'
            ) {
              return SUBSCRIPTION_LABEL[serviceType].name;
            }
            if (featureEnabled('keyholderBillingFeature')) {
              if (
                (type === 'LIMITED' || type === 'MANAGED_ACCESS') &&
                subRole
              ) {
                return `${SUBSCRIPTION_LABEL[type].name}-${userSubRolesToLabelMap[subRole]}`;
              }
              return SUBSCRIPTION_LABEL[type].name;
            }
            // In any other cases, use the Answering Service 'type'
            return SUBSCRIPTION_LABEL[type].name;
          };

          const shouldDisplayZero = () => {
            let shouldDisplay = false;
            // If the onboarding portal does not have a mlsProductId and the
            // subscription is not in an 'onHold' state, the user should
            // just be shown $0 if their Board/MLS is covering the payment.
            if (!hasValidStripeMlsProductId) {
              if (status !== 'onHold') {
                if (isMlsCovered || isBoardCovered) {
                  shouldDisplay = true;
                }
              }
            }
            return shouldDisplay;
          };

          const shouldDisplayPrice = () => {
            let shouldDisplay = false;
            // If the onboarding portal does not have an mlsProductId, only show the price if
            // the subscription is not in an 'onHold' state and if it has a price.
            if (status !== 'onHold') {
              if (Boolean(price) || price === 0) {
                // If they DO have a valid mlsProductId, we shouldn't show the price
                // UNLESS they are paying for LiveConciergePlus but useLiveConciergePlus is off.
                if (
                  (type !== 'answering' && type !== 'default') ||
                  !hasValidStripeMlsProductId ||
                  (serviceType === 'LiveConciergePlus' && !useLiveConciergePlus)
                ) {
                  shouldDisplay = true;
                }
              }
            }
            return shouldDisplay;
          };

          const shouldDisplayCancelButton = () => {
            let shouldDisplay = false;
            // The cancel button should only appear if there is no mlsProductId
            // or if they are paying for LiveConciergePlus but conciergeEnabled & useLiveConciergePlus are off.
            if (id) {
              if (
                (!hasValidStripeMlsProductId && type === 'answering') ||
                (serviceType === 'LiveConciergePlus' &&
                  !(conciergeEnabled && useLiveConciergePlus))
              ) {
                shouldDisplay = true;
              }
            }
            return shouldDisplay;
          };

          return (
            <StyledSubscriptionContainer key={id}>
              <StyledIconContainer>
                {
                  // Swap the icon depending on the subscription type
                  featureEnabled('keyholderBillingFeature') &&
                  (type === 'FULL' ||
                    type === 'LIMITED' ||
                    type === 'MANAGED_ACCESS') ? (
                    <WebElementsIcon
                      icon="keyOutlined"
                      size={15}
                      color="primary"
                    />
                  ) : (
                    <Icon type="antdPhoneFilled" width={15} color="primary" />
                  )
                }
              </StyledIconContainer>
              <StyledNameContainer>
                <div>
                  <StyledName>{displayCorrectSubscriptionName()}</StyledName>
                </div>
                {(!hasValidStripeMlsProductId || !serviceType) && (
                  <Text type="secondary">
                    {SUBSCRIPTION_LABEL[type].subHeader}
                  </Text>
                )}
              </StyledNameContainer>
              {type !== 'default' && (
                <>
                  {/* Display the price of this subscription. */}
                  <StyledMarginAuto>
                    {shouldDisplayZero() ? (
                      <Text strong>$0</Text>
                    ) : (
                      shouldDisplayPrice() && (
                        <Text strong>
                          ${price!} / {billingFrequency}
                        </Text>
                      )
                    )}
                    {status === 'onHold' && (
                      <Text strong>ON HOLD (BROKERAGE HAS SUBSCRIBED)</Text>
                    )}
                  </StyledMarginAuto>
                  {/* Display the cancel button. */}
                  {shouldDisplayCancelButton() && (
                    <Button
                      onClick={() => onCancelSubscription(id!)}
                      disabled={cancellingSubscription}
                    >
                      Cancel Subscription
                    </Button>
                  )}
                </>
              )}
              {type === 'default' && showPlanManage && (
                <StyledManageButton
                  onClick={() => {
                    const { response, message } = canManageSubscription;
                    if (!response) {
                      AntdModal.error({
                        title:
                          'You are not authorized to manage subscriptions.',
                        content: message,
                        centered: true,
                      });
                      return;
                    }
                    setManagePlanOpen(true);
                  }}
                  disabled={cancellingSubscription}
                  data-cy="manage-plan-button"
                >
                  Manage
                </StyledManageButton>
              )}
            </StyledSubscriptionContainer>
          );
        })
      ) : (
        <StyledNoContent>No subscriptions found</StyledNoContent>
      ),
    [
      subscriptions,
      isMlsCovered,
      isBoardCovered,
      useLiveConciergePlus,
      hasValidStripeMlsProductId,
      conciergeEnabled,
      showPlanManage,
      cancellingSubscription,
      canManageSubscription,
      orgPlanPrice,
      onCancelSubscription,
    ],
  );

  // The actual components we will render
  return (
    <Card title="Subscriptions">
      {loadingSubscription ? (
        <StyledCentre>
          <Spin />
        </StyledCentre>
      ) : (
        renderSubscriptions
      )}
      <ManagePlanModal
        visible={isManagePlanOpen}
        onClose={() => setManagePlanOpen(false)}
        orgPlanPrice={orgPlanPrice}
      />
    </Card>
  );
};

const StyledCentre = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledNoContent = styled(Text)`
  color: ${theme.getColor('textSecondary')};
`;

const StyledSubscriptionContainer = styled.div`
  height: 76px;
  margin-bottom: 13px;
  border: 1px solid #f5f5f5;
  display: flex;
  align-items: center;
  padding: 0 22px;
`;

const StyledIconContainer = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #f0f5ff;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 15px;
`;

const StyledNameContainer = styled.div`
  margin-right: 70px;
`;

const StyledName = styled(Text)`
  font-size: 16px !important;
`;

const StyledMarginAuto = styled.div`
  margin-right: auto;
`;

const StyledManageButton = styled(Button)`
  margin-left: auto;
`;

export default Content;
